import { PropsWithChildren, useMemo } from "react";

import { InfoCircledIcon } from "@radix-ui/react-icons";
import {
  Popover as RadixPopover,
  Grid,
  Inset,
  Heading,
  Text,
  Flex,
} from "@radix-ui/themes";
import { PopoverContentOwnProps } from "@radix-ui/themes/dist/cjs/components/popover.props";
import { styled } from "@stitches/react";

import { Button } from "../../../common/components/Button";

type Props = {
  title: string;
  content: string;
  closeLabel?: string;
  variant?: "info" | "warning" | "error";
  onClose?: () => void;
};

type PopoverProps = PropsWithChildren<Props> & PopoverContentOwnProps;

const StyledInset = styled(Inset, {
  color: "var(--accent-contrast)",
  backgroundColor: "var(--accent-9)",

  variants: {
    variant: {
      info: {
        backgroundColor: "var(--accent-9)",
        color: "var(--accent-contrast)",
      },
      warning: {
        backgroundColor: "var(--amber-10)",
        color: "var(--amber-contrast)",
      },
      error: {
        backgroundColor: "var(--error-9)",
        color: "var(--error-contrast)",
      },
    },
  },
});

export const Popover = ({
  children,
  title,
  content,
  closeLabel,
  variant,
  onClose,
  ...popoverProps
}: PopoverProps) => {
  const radixColorFromVariant = useMemo(() => {
    switch (variant) {
      case "warning":
        return "amber";
      case "error":
        return "red";
      case "info":
      default:
        return "iris";
    }
  }, [variant]);

  return (
    <RadixPopover.Root {...popoverProps}>
      <RadixPopover.Trigger>{children}</RadixPopover.Trigger>
      <RadixPopover.Content
        sideOffset={3}
        size="1"
        side="bottom"
        align="start"
        alignOffset={8}
        style={{ width: "300px" }}
      >
        <Grid columns="24px 1fr" gap="4">
          <StyledInset
            side="left"
            variant={variant}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <InfoCircledIcon width="24px" height="24px" />
          </StyledInset>
          <div>
            <Heading size="2" mb="1">
              {title}
            </Heading>
            <Text as="p" size="2" mb="4" color="gray">
              {content}
            </Text>

            <Flex direction="column" align="stretch">
              <RadixPopover.Close>
                <Button
                  variant="outline"
                  color={radixColorFromVariant}
                  title={closeLabel ?? "Close"}
                  onClick={onClose}
                >
                  {closeLabel ?? "Close"}
                </Button>
              </RadixPopover.Close>
            </Flex>
          </div>
        </Grid>
      </RadixPopover.Content>
    </RadixPopover.Root>
  );
};
